import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/Card';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { useCurrentFarm } from '@/hooks/useCurrentFarm';
import {
  ArrowLeft,
  CheckCircle,
  XCircle,
  AlertTriangle,
  FileSpreadsheet,
  Droplets,
  Cookie,
  Plus,
  RefreshCw,
} from 'lucide-react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { RoleGuard } from '@/components/layout/RoleGuard';

export const Route = createFileRoute('/_app/import/$jobId')({
  component: () => (
    <RoleGuard allowedRoles={['owner', 'supervisor', 'operator']}>
      <ImportJobDetailPage />
    </RoleGuard>
  ),
});

interface ImportJobDetail {
  id: string;
  filename: string;
  status: string;
  sheets_found: number;
  total_rows: number;
  valid_rows: number;
  error_rows: number;
  ponds_created: string[];
  ponds_matched: string[];
  water_readings_imported: number;
  feedings_imported: number;
  error_message: string | null;
  validation_errors: Array<{ sheet: string; row: number; error: string }>;
  created_at: string;
  processing_completed_at: string | null;
}

interface ImportRecord {
  id: string;
  sheet_name: string;
  row_number: number;
  record_type: 'water_reading' | 'feeding';
  parsed_data: Record<string, unknown>;
  is_valid: boolean;
  validation_warnings: Array<{ field: string; message: string }>;
  validation_errors: Array<{ field: string; message: string }>;
}

function ImportJobDetailPage() {
  const { jobId } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { farmId, isLoading: isFarmLoading } = useCurrentFarm();
  const [activeTab, setActiveTab] = useState<'water_reading' | 'feeding'>('water_reading');
  const [createPonds, setCreatePonds] = useState(true);

  const {
    data: job,
    isLoading: jobLoading,
    error: jobError,
  } = useQuery<ImportJobDetail>({
    queryKey: ['import', 'job', jobId, farmId],
    queryFn: () => api.get(`/api/v1/farms/${farmId}/import/jobs/${jobId}`),
    enabled: !!farmId,
  });

  const {
    data: records,
    isLoading: recordsLoading,
  } = useQuery<ImportRecord[]>({
    queryKey: ['import', 'records', jobId, activeTab, farmId],
    queryFn: () =>
      api.get(`/api/v1/farms/${farmId}/import/jobs/${jobId}/records`, {
        params: { record_type: activeTab, limit: '100' },
      }),
    enabled: !!farmId && !!job && job.status === 'ready_for_review',
  });

  const confirmMutation = useMutation({
    mutationFn: () =>
      api.post(`/api/v1/farms/${farmId}/import/jobs/${jobId}/confirm`, {
        create_missing_ponds: createPonds,
      }),
    onSuccess: () => {
      toast.success('Import completed successfully');
      queryClient.invalidateQueries({ queryKey: ['import'] });
      navigate({ to: '/import' });
    },
    onError: (error: Error) => {
      toast.error(`Import failed: ${error.message}`);
    },
  });

  const cancelMutation = useMutation({
    mutationFn: () =>
      api.post(`/api/v1/farms/${farmId}/import/jobs/${jobId}/cancel`),
    onSuccess: () => {
      toast.success('Import cancelled');
      queryClient.invalidateQueries({ queryKey: ['import'] });
      navigate({ to: '/import' });
    },
    onError: (error: Error) => {
      toast.error(`Failed to cancel: ${error.message}`);
    },
  });

  if (isFarmLoading || jobLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size={32} />
      </div>
    );
  }

  if (jobError || !job) {
    return (
      <div className="space-y-4">
        <Link to="/import" className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-700">
          <ArrowLeft className="h-4 w-4" />
          Back to Import
        </Link>
        <Card className="border-red-200">
          <Card.Body className="text-center py-8">
            <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="font-semibold text-gray-900">Import Job Not Found</h2>
            <p className="text-gray-500 mt-1">
              This import job may have been deleted or does not exist.
            </p>
          </Card.Body>
        </Card>
      </div>
    );
  }

  const isReadyForReview = job.status === 'ready_for_review';
  const isCompleted = job.status === 'completed';
  const isFailed = job.status === 'failed';
  const isProcessing = job.status === 'processing' || job.status === 'importing';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/import" className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <ArrowLeft className="h-5 w-5 text-gray-500" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{job.filename}</h1>
            <p className="text-sm text-gray-500">
              Uploaded {format(new Date(job.created_at), 'MMM d, yyyy h:mm a')}
            </p>
          </div>
        </div>
        <StatusBadge status={job.status} />
      </div>

      {/* Summary Stats */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          icon={FileSpreadsheet}
          label="Sheets"
          value={job.sheets_found}
          color="text-blue-500"
          bg="bg-blue-100"
        />
        <StatCard
          icon={CheckCircle}
          label="Valid Rows"
          value={job.valid_rows}
          color="text-green-500"
          bg="bg-green-100"
        />
        <StatCard
          icon={AlertTriangle}
          label="Errors"
          value={job.error_rows}
          color="text-red-500"
          bg="bg-red-100"
        />
        <StatCard
          icon={Plus}
          label="New Ponds"
          value={job.ponds_created.length}
          color="text-amber-500"
          bg="bg-amber-100"
        />
      </div>

      {/* Pond Mapping */}
      {(job.ponds_created.length > 0 || job.ponds_matched.length > 0) && (
        <Card>
          <Card.Header>
            <h2 className="font-semibold">Pond Mapping</h2>
          </Card.Header>
          <Card.Body className="space-y-4">
            {job.ponds_matched.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">
                  Matched to Existing Ponds ({job.ponds_matched.length})
                </h3>
                <div className="flex flex-wrap gap-2">
                  {job.ponds_matched.map((name) => (
                    <span
                      key={name}
                      className="px-2 py-1 bg-green-50 text-green-700 rounded text-sm"
                    >
                      {name}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {job.ponds_created.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">
                  New Ponds to Create ({job.ponds_created.length})
                </h3>
                <div className="flex flex-wrap gap-2">
                  {job.ponds_created.map((name) => (
                    <span
                      key={name}
                      className="px-2 py-1 bg-amber-50 text-amber-700 rounded text-sm"
                    >
                      {name}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      {/* Completed Stats */}
      {isCompleted && (
        <Card className="border-green-200 bg-green-50">
          <Card.Body>
            <div className="flex items-center gap-4">
              <CheckCircle className="h-8 w-8 text-green-500" />
              <div>
                <h2 className="font-semibold text-green-800">Import Completed</h2>
                <p className="text-green-700">
                  {job.water_readings_imported} water readings and {job.feedings_imported} feedings imported successfully.
                </p>
              </div>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Failed Error */}
      {isFailed && job.error_message && (
        <Card className="border-red-200 bg-red-50">
          <Card.Body>
            <div className="flex items-center gap-4">
              <XCircle className="h-8 w-8 text-red-500" />
              <div>
                <h2 className="font-semibold text-red-800">Import Failed</h2>
                <p className="text-red-700">{job.error_message}</p>
              </div>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Processing State */}
      {isProcessing && (
        <Card className="border-blue-200 bg-blue-50">
          <Card.Body>
            <div className="flex items-center gap-4">
              <RefreshCw className="h-8 w-8 text-blue-500 animate-spin" />
              <div>
                <h2 className="font-semibold text-blue-800">Processing...</h2>
                <p className="text-blue-700">
                  Please wait while we process your import.
                </p>
              </div>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Record Preview */}
      {isReadyForReview && (
        <Card>
          <Card.Header>
            <div className="flex items-center justify-between">
              <h2 className="font-semibold">Preview Records</h2>
              <div className="flex gap-2">
                <button
                  onClick={() => setActiveTab('water_reading')}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === 'water_reading'
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <Droplets className="h-4 w-4" />
                  Water
                </button>
                <button
                  onClick={() => setActiveTab('feeding')}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === 'feeding'
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <Cookie className="h-4 w-4" />
                  Feedings
                </button>
              </div>
            </div>
          </Card.Header>
          <Card.Body className="p-0">
            {recordsLoading ? (
              <div className="flex items-center justify-center py-12">
                <LoadingSpinner size={24} />
              </div>
            ) : !records || records.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                No {activeTab === 'water_reading' ? 'water readings' : 'feedings'} found
              </div>
            ) : (
              <div className="overflow-x-auto">
                <RecordTable records={records} type={activeTab} />
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      {/* Action Buttons */}
      {isReadyForReview && (
        <Card>
          <Card.Body>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="createPonds"
                  checked={createPonds}
                  onChange={(e) => setCreatePonds(e.target.checked)}
                  className="h-4 w-4 text-primary-600 rounded border-gray-300"
                />
                <label htmlFor="createPonds" className="text-sm text-gray-700">
                  Create missing ponds ({job.ponds_created.length} new)
                </label>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => cancelMutation.mutate()}
                  disabled={cancelMutation.isPending}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => confirmMutation.mutate()}
                  disabled={confirmMutation.isPending}
                  className="px-4 py-2 text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {confirmMutation.isPending && <LoadingSpinner size={16} />}
                  Confirm Import
                </button>
              </div>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Validation Errors */}
      {job.validation_errors.length > 0 && (
        <Card>
          <Card.Header>
            <h2 className="font-semibold text-red-600">Validation Errors</h2>
          </Card.Header>
          <Card.Body className="p-0">
            <div className="max-h-64 overflow-y-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Sheet</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Row</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Error</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {job.validation_errors.slice(0, 50).map((err, idx) => (
                    <tr key={idx} className="text-sm">
                      <td className="px-4 py-2 text-gray-900">{err.sheet}</td>
                      <td className="px-4 py-2 text-gray-500">{err.row}</td>
                      <td className="px-4 py-2 text-red-600">{err.error}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {job.validation_errors.length > 50 && (
                <div className="px-4 py-2 text-sm text-gray-500 bg-gray-50">
                  Showing 50 of {job.validation_errors.length} errors
                </div>
              )}
            </div>
          </Card.Body>
        </Card>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const configs: Record<string, { label: string; color: string }> = {
    pending: { label: 'Pending', color: 'bg-gray-100 text-gray-700' },
    processing: { label: 'Processing', color: 'bg-blue-100 text-blue-700' },
    ready_for_review: { label: 'Ready for Review', color: 'bg-amber-100 text-amber-700' },
    importing: { label: 'Importing', color: 'bg-blue-100 text-blue-700' },
    completed: { label: 'Completed', color: 'bg-green-100 text-green-700' },
    failed: { label: 'Failed', color: 'bg-red-100 text-red-700' },
    cancelled: { label: 'Cancelled', color: 'bg-gray-100 text-gray-500' },
  };

  const config = configs[status] || configs.pending;

  return (
    <span className={`px-3 py-1 rounded-full text-sm font-medium ${config.color}`}>
      {config.label}
    </span>
  );
}

function StatCard({
  icon: Icon,
  label,
  value,
  color,
  bg,
}: {
  icon: typeof CheckCircle;
  label: string;
  value: number;
  color: string;
  bg: string;
}) {
  return (
    <Card>
      <Card.Body className="flex items-center gap-4">
        <div className={`p-3 rounded-lg ${bg}`}>
          <Icon className={`h-6 w-6 ${color}`} />
        </div>
        <div>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          <p className="text-sm text-gray-500">{label}</p>
        </div>
      </Card.Body>
    </Card>
  );
}

function RecordTable({
  records,
  type,
}: {
  records: ImportRecord[];
  type: 'water_reading' | 'feeding';
}) {
  if (type === 'water_reading') {
    return (
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Sheet</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Date</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Slot</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">pH</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">DO</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Transparency</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Alkalinity</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Weather</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {records.map((r) => (
            <tr key={r.id} className={!r.is_valid ? 'bg-red-50' : ''}>
              <td className="px-3 py-2 text-gray-900">{r.sheet_name}</td>
              <td className="px-3 py-2 text-gray-600">
                {r.parsed_data.reading_date as string}
              </td>
              <td className="px-3 py-2 text-gray-600">
                {r.parsed_data.reading_slot as string}
              </td>
              <td className="px-3 py-2 text-gray-600">
                {r.parsed_data.ph?.toString() || '-'}
              </td>
              <td className="px-3 py-2 text-gray-600">
                {r.parsed_data.dissolved_oxygen?.toString() || '-'}
              </td>
              <td className="px-3 py-2 text-gray-600">
                {r.parsed_data.transparency_cm?.toString() || '-'}
              </td>
              <td className="px-3 py-2 text-gray-600">
                {(r.parsed_data.alkalinity_am || r.parsed_data.alkalinity_pm)
                  ? `${r.parsed_data.alkalinity_am || '-'}/${r.parsed_data.alkalinity_pm || '-'}`
                  : '-'}
              </td>
              <td className="px-3 py-2 text-gray-600">
                {r.parsed_data.weather as string || '-'}
              </td>
              <td className="px-3 py-2">
                {r.is_valid ? (
                  <CheckCircle className="h-4 w-4 text-green-500" />
                ) : (
                  <XCircle className="h-4 w-4 text-red-500" />
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  return (
    <table className="min-w-full divide-y divide-gray-200 text-sm">
      <thead className="bg-gray-50">
        <tr>
          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Sheet</th>
          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Date</th>
          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Slot</th>
          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Amount (kg)</th>
          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Tray Check</th>
          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Type</th>
          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Status</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200">
        {records.map((r) => (
          <tr key={r.id} className={!r.is_valid ? 'bg-red-50' : ''}>
            <td className="px-3 py-2 text-gray-900">{r.sheet_name}</td>
            <td className="px-3 py-2 text-gray-600">
              {r.parsed_data.feeding_date as string}
            </td>
            <td className="px-3 py-2 text-gray-600">
              {r.parsed_data.feeding_slot as string}
            </td>
            <td className="px-3 py-2 text-gray-600">
              {(r.parsed_data.feed_amount_kg as number)?.toFixed(2) || '-'}
            </td>
            <td className="px-3 py-2 text-gray-600">
              {r.parsed_data.feed_tray_check as string || '-'}
            </td>
            <td className="px-3 py-2 text-gray-600">
              {r.parsed_data.feed_type as string || '-'}
            </td>
            <td className="px-3 py-2">
              {r.is_valid ? (
                <CheckCircle className="h-4 w-4 text-green-500" />
              ) : (
                <XCircle className="h-4 w-4 text-red-500" />
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
